<template>
  <b-card>
    <b-card-title class="mb-1">
      <feather-icon
        icon="ShieldIcon"
        size="24"
        class="mr-1"
      />
      Rolle & Berechtigungen
    </b-card-title>

    <b-card-body>
      <!-- Role Information -->
      <div class="mb-3">
        <div class="d-flex align-items-center mb-2">
          <feather-icon
            icon="BriefcaseIcon"
            size="20"
            class="mr-1 text-primary"
          />
          <h5 class="mb-0">
            {{ user.role ? user.role.name : 'Keine Rolle zugewiesen' }}
          </h5>
        </div>
        <p
          v-if="user.role && user.role.description"
          class="text-muted"
        >
          {{ user.role.description }}
        </p>
        <p
          v-else
          class="text-muted"
        >
          Keine Rollenbeschreibung verfügbar
        </p>
      </div>

      <!-- Role Details Table -->
      <table class="w-100 mb-3">
        <tbody>
          <tr>
            <td
              class="pb-1"
              width="40%"
            >
              <feather-icon
                icon="TagIcon"
                size="14"
                class="mr-1"
              />
              <span class="font-weight-bold">Rollen-ID</span>
            </td>
            <td class="pb-1">
              {{ user.role ? user.role.id : 'N/A' }}
            </td>
          </tr>
          <tr>
            <td class="pb-1">
              <feather-icon
                icon="TypeIcon"
                size="14"
                class="mr-1"
              />
              <span class="font-weight-bold">Rollen-Typ</span>
            </td>
            <td class="pb-1">
              {{ user.role ? user.role.type : 'N/A' }}
            </td>
          </tr>
          <tr>
            <td class="pb-1">
              <feather-icon
                icon="LockIcon"
                size="14"
                class="mr-1"
              />
              <span class="font-weight-bold">Zugriffslevel</span>
            </td>
            <td class="pb-1">
              <b-badge :variant="accessLevelVariant">
                {{ accessLevel }}
              </b-badge>
            </td>
          </tr>
          <tr v-if="user.role && user.role.permissions">
            <td class="pb-1">
              <feather-icon
                icon="KeyIcon"
                size="14"
                class="mr-1"
              />
              <span class="font-weight-bold">Berechtigungen</span>
            </td>
            <td class="pb-1">
              {{ user.role.permissions.length }} zugewiesen
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Permissions Section -->
      <div v-if="user.role && user.role.permissions">
        <h6 class="mb-2">
          <feather-icon
            icon="ListIcon"
            size="16"
            class="mr-1"
          />
          Detaillierte Berechtigungen
        </h6>
        <div class="permissions-container">
          <div
            v-for="permission in user.role.permissions"
            :key="permission.id"
            class="permission-item mb-1"
          >
            <b-badge
              variant="light-info"
              class="mr-1 mb-50"
            >
              <feather-icon
                icon="CheckIcon"
                size="12"
                class="mr-25"
              />
              {{ permission.action }}
            </b-badge>
          </div>
        </div>
      </div>

      <!-- Security Information -->
      <div class="mt-3 pt-2 border-top">
        <h6 class="mb-2">
          <feather-icon
            icon="ShieldCheckIcon"
            size="16"
            class="mr-1"
          />
          Sicherheitseinstellungen
        </h6>

        <div class="d-flex flex-wrap">
          <div class="security-item mr-3 mb-2">
            <small class="text-muted d-block">E-Mail bestätigt</small>
            <b-badge :variant="user.confirmed ? 'success' : 'warning'">
              <feather-icon
                :icon="user.confirmed ? 'CheckIcon' : 'AlertCircleIcon'"
                size="12"
                class="mr-25"
              />
              {{ user.confirmed ? 'Ja' : 'Nein' }}
            </b-badge>
          </div>

          <div class="security-item mr-3 mb-2">
            <small class="text-muted d-block">Konto gesperrt</small>
            <b-badge :variant="user.blocked ? 'danger' : 'success'">
              <feather-icon
                :icon="user.blocked ? 'LockIcon' : 'UnlockIcon'"
                size="12"
                class="mr-25"
              />
              {{ user.blocked ? 'Ja' : 'Nein' }}
            </b-badge>
          </div>

          <div class="security-item mb-2">
            <small class="text-muted d-block">Letzter Login</small>
            <span class="text-body">
              {{ user.lastLogin ? getFormattedDate(user.lastLogin) : 'Nie' }}
            </span>
          </div>
        </div>
      </div>

    </b-card-body>
  </b-card>
</template>

<script>
import {
  BCard,
  BCardTitle,
  BCardBody,
  BBadge,
} from 'bootstrap-vue'
import helper from '@/helper/helper'

export default {
  components: {
    BCard,
    BCardTitle,
    BCardBody,
    BBadge,
  },
  props: {
    user: {
      type: Object,
      required: true,
    },
  },
  setup() {
    const { getFormattedDate } = helper()
    return { getFormattedDate }
  },
  computed: {
    accessLevel() {
      if (!this.user.role) return 'Keine Rolle'

      const roleName = this.user.role.name.toLowerCase()
      if (roleName.includes('admin')) return 'Administrator'
      if (roleName.includes('manager')) return 'Manager'
      if (roleName.includes('user')) return 'Benutzer'
      if (roleName.includes('guest')) return 'Gast'

      return this.user.role.name
    },
    accessLevelVariant() {
      const level = this.accessLevel.toLowerCase()
      if (level.includes('administrator')) return 'danger'
      if (level.includes('manager')) return 'warning'
      if (level.includes('benutzer')) return 'primary'
      if (level.includes('gast')) return 'secondary'

      return 'info'
    },
  },
}
</script>

<style lang="scss" scoped>
.font-weight-bold {
  font-weight: 600;
}

.permissions-container {
  max-height: 200px;
  overflow-y: auto;
}

.permission-item {
  display: inline-block;
}

.security-item {
  min-width: 120px;
}

.border-top {
  border-top: 1px solid #ebe9f1 !important;
}

table td {
  vertical-align: middle;
}
</style>
